import {
  AccelerantSubmission,
  AccelerantPayload,
  AccelerantResponse,
  AttachmentUploadResult,
  ReferralStatusUpdateResult,
  AccelerantReferral,
} from "./types/Accelerant.types";
import { Result } from "@badrap/result";
import { getUnderwriterId } from "../../utils/underwriterMapping";
import { config } from "../../config/env";

export class AccelerantService {
  private apiKey: string;

  constructor() {
    this.apiKey = config.n8n?.apiKey || "";
  }

  async submitApplication(
    data: AccelerantSubmission
  ): Promise<Result<AccelerantResponse, Error>> {
    try {
      const referralReasonMap: { [key: string]: string } = {
        "Hazard Exposure": "RRM-001025",
        Limits: "RRM-001034",
        "Claims Experience": "RRM-001010",
      };

      const mappedReferralReasons = data.referralReasons.map(
        (reason) => referralReasonMap[reason] || reason
      );

      let referralType: string;
      if (data.applicationType === "NEW") {
        referralType = "referral_new_business";
      } else if (data.applicationType === "REN") {
        referralType = "accelerant_renewal_referral";
      } else if (data.applicationType === "END") {
        referralType = "accelerant_mta_referral";
      } else {
        referralType = "referral_new_business";
      }

      const inceptionDate = data.effectiveDate
        ? new Date(data.effectiveDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      const expiryDate = data.expirationDate
        ? new Date(data.expirationDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      const requestedBy = new Date();
      requestedBy.setDate(requestedBy.getDate() + 2);

      let programId = "";
      let products = ["PRO-000026"];
      let linesOfBusiness = ["LOB-000020", "LOB-000011"];

      if (data.policySystem === "surefyre") {
        const programName = data.program?.toUpperCase();

        if (programName === "FLEX") {
          programId = "MPP-001099";
          products = ["PRO-000026"];
          linesOfBusiness = ["LOB-000011"];
        } else if (programName === "COMMERCIAL") {
          programId = "MPP-001032";
          products = ["PRO-000023"];
          linesOfBusiness = ["LOB-000011"];
        } else {
          programId = "MPP-001099";
        }
      }

      const underwriterId = data.underwriter
        ? getUnderwriterId(data.underwriter)
        : undefined;

      const payload: AccelerantPayload = {
        org_id: "ca90e8a7-322a-427a-aeb9-f91160d1fe36",
        member_underwriter_id: underwriterId || "8ce655b4-cb8f-4233-b72e-f07f241bd552",
        accelerant_underwriter_id: "57f3c55d-98a8-42dd-a32c-079f5a57cbe5",
        referral_type: referralType,
        state: "draft",
        insured_name: data.namedInsured,
        quote_number: data.quote,
        program_id: programId,
        products: products,
        lines_of_business: linesOfBusiness,
        referral_reasons: mappedReferralReasons,
        inception_date: inceptionDate,
        expiry_date: expiryDate,
        requested_by: requestedBy.toISOString(),
        summary_of_risk: data.summaryOfRisk,
        policy_currency: "USD",
        total_sums_insured: data.limit || 0,
      };

      const apiUrl = config.accelerant.submitReferralUrl;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorDetails = "";
        try {
          const errorBody = await response.text();
          errorDetails = errorBody;
        } catch (e) {
          // Ignore parse error
        }
        return Result.err(
          new Error(
            `HTTP ${response.status}: ${response.statusText}. ${errorDetails}`
          )
        );
      }

      const result = await response.json();
      return Result.ok({
        success: true,
        referenceNumber: result.referenceNumber || result.id,
        referralId: result.id,
        message: result.message || "Application submitted successfully",
      });
    } catch (error) {
      return Result.err(
        error instanceof Error
          ? error
          : new Error("Failed to submit application")
      );
    }
  }

  async uploadAttachments(
    referralId: string,
    files: { url: string; name: string }[]
  ): Promise<Result<AttachmentUploadResult, Error>> {
    try {
      const apiUrl = `${config.accelerant.uploadAttachmentsUrl}/${referralId}`;
      const n8nFileProxyUrl = config.accelerant.fileProxyUrl;
      
      if (!n8nFileProxyUrl) {
        return Result.err(
          new Error("N8n file proxy webhook URL is not set")
        );
      }

      const uploadPromises = files.map(async (file) => {
        try {
          const formData = new FormData();
          
          const fileResponse = await fetch(n8nFileProxyUrl, {
            method: "POST",
            headers: {
              "X-API-Key": this.apiKey,
            },
            body: JSON.stringify({
              url: file.url,
            }),
          });
          
          if (!fileResponse.ok) {
            return {
              success: false,
              fileName: file.name,
              status: 'failed' as const
            };
          }

          const blob = await fileResponse.blob();

          let mimeType = blob.type;
          console.log("Mime type", mimeType);
          if (!mimeType || mimeType === "application/octet-stream") {
            const extension = file.name.split(".").pop()?.toLowerCase();
            switch (extension) {
              case "png":
                mimeType = "image/png";
                break;
              case "jpg":
              case "jpeg":
                mimeType = "image/jpeg";
                break;
              case "pdf":
                mimeType = "application/pdf";
                break;
              case "doc":
                mimeType = "application/msword";
                break;
              case "docx":
                mimeType =
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                break;
              default:
                mimeType = "application/pdf";
            }
          }

          const fileObj = new File([blob], file.name, { type: mimeType });
          formData.append("file", fileObj);
          const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "X-API-Key": this.apiKey,
            },
            body: formData,
          });

          const result = await response.json();

          if (result.error) {
            try {
              if (result.error.message.includes("duplicate_attachment")) {
                return {
                  success: true,
                  fileName: file.name,
                  status: 'ready' as const
                };
              }
            } catch (e) {}
            return {
              success: false,
              fileName: file.name,
              status: 'failed' as const
            };
          } else {
            if (result.status === "ready") {
              return {
                success: true,
                fileName: file.name,
                status: 'ready' as const
              };
            } else {
              return {
                success: false,
                fileName: file.name,
                status: 'pending' as const
              };
            }
          }
        } catch (error) {
          return {
            success: false,
            fileName: file.name,
            status: 'failed' as const
          };
        }
      });

      const uploadResults = await Promise.all(uploadPromises);
      
      let successCount = uploadResults.filter(r => r.success).length;
      const failedFiles = uploadResults
        .filter(r => !r.success && r.status === 'failed')
        .map(r => r.fileName);
      const failedStatus = uploadResults.some(r => r.status === 'pending');

      if (failedStatus) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        const getReferralResult = await this.getReferralById(referralId);
        if (getReferralResult.isErr) {
          return Result.err(new Error("Failed to get referral details"));
        }
        const referral = getReferralResult.value;
        if (
          referral.attachments.every(
            (attachment) => attachment.status === "ready"
          )
        ) {
          successCount = referral.attachments.length;
        }
      }

      const isSuccess = successCount === files.length;

      const result: AttachmentUploadResult = {
        success: isSuccess,
        message:
          successCount > 0
            ? `Successfully uploaded ${successCount}/${files.length} attachments`
            : undefined,
        error: isSuccess
          ? undefined
          : `Failed to upload ${failedFiles.join(", ")}`,
      };

      return isSuccess
        ? Result.ok(result)
        : Result.err(new Error("Failed to upload attachments"));
    } catch (error) {
      return Result.err(
        error instanceof Error
          ? error
          : new Error("Failed to upload attachments")
      );
    }
  }

  async getReferralById(
    referralId: string
  ): Promise<Result<AccelerantReferral, Error>> {
    try {
      const apiUrl = `${config.accelerant.getReferralUrl}/${referralId}`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
      });

      if (!response.ok) {
        let errorDetails = "";
        try {
          const errorBody = await response.text();
          errorDetails = errorBody;
        } catch (e) {
          console.error("Could not parse error response");
        }
        return Result.err(
          new Error(
            `Failed to fetch referral: HTTP ${response.status}. ${errorDetails}`
          )
        );
      }

      const result = await response.json();
      return Result.ok(result);
    } catch (error) {
      return Result.err(
        error instanceof Error ? error : new Error("Failed to fetch referral")
      );
    }
  }

  async updateReferralStatus(
    referralId: string,
    status: string
  ): Promise<Result<ReferralStatusUpdateResult, Error>> {
    try {
      console.log(
        "Updating referral status",
        referralId,
        status,
        `${process.env.REACT_APP_N8N_ACCELERANT_UPDATE_REFERRAL}/${referralId}`
      );
      const apiUrl = `${config.accelerant.updateReferralUrl}/${referralId}`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
        body: JSON.stringify({
          state: status,
        }),
      });
      console.log("Response", await response.json());

      if (!response.ok) {
        let errorDetails = "";
        try {
          const errorBody = await response.text();
          errorDetails = errorBody;
        } catch (e) {
          console.error("Could not parse error response");
        }
        return Result.err(
          new Error(
            `Failed to update referral status: HTTP ${response.status}. ${errorDetails}`
          )
        );
      }

      return Result.ok({
        success: true,
        message: `Successfully updated referral status to ${status}`,
      });
    } catch (error) {
      console.error("Failed to update referral status", error);
      return Result.err(
        error instanceof Error
          ? error
          : new Error("Failed to update referral status")
      );
    }
  }
}

export const accelerantService = new AccelerantService();
